<template> 
    <div v-if="isTeacher || user.my_courses.length">
        <v-card v-if="homeData.enrollments_count === 0" class="soft-shadow mb-3">
            <v-card-text class="pa-3">
                <v-alert small text type="info" icon="mdi-book-play" color="success" class="px-3 mb-0">
                    <div>
                        <b class="text--darken-1 success--text">Account created successfully. </b>
                        <div class="medium-text font-weight-medium">
                            <div class="strong-text--text">
                                Now you should enroll in a course to get a <strong class="primary--text">free trial</strong> classes.
                            </div>
                        </div>
                    </div>
                    <div class="mt-3">
                        <v-btn depressed color="success" style="text-transform: none" :to="{name: 'courses'}">
                            <v-icon left>mdi-view-grid-outline</v-icon>
                            <b>Discover Courses</b>
                            <v-icon right size="18" class="ms-1" style="opacity: 0.5">mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                </v-alert>
            </v-card-text>
        </v-card>

        <!-- Header Slider -->
        <div v-if="slideImages && slideImages.length" class="mb-4 mb-lg-10">
            <v-carousel :show-arrows-on-hover="slideImages.length > 1" cycle :height="pcOnly ? 450 : 150" :show-arrows="slideImages.length > 1" :continuous="true" class="header-carousel" :hide-delimiters="slideImages.length <= 1" :class="{'bordered': (!slideImages || !slideImages.length)}">
                <template v-slot:prev="{ on, attrs }">
                    <v-btn large fab v-bind="attrs" v-on="on" class="transparent" depressed>
                        <v-icon large>mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
                <template v-slot:next="{ on, attrs }">
                    <v-btn large fab v-bind="attrs" v-on="on" class="transparent" depressed>
                        <v-icon large>mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
                <v-carousel-item v-for="(img,i) in slideImages" :key="i">
                    <a v-if="img.href" :href="img.href" :target="capRunning ? '_system' : '_blank'">
                        <div :style="`width: 100%; height: 100%; background-image: url(${img.url}); background-size: cover; background-position: center;`" class="p-relative">
                            <v-btn fab :small="pcOnly" :x-small="phoneOnly" absolute top right depressed  style="top: 10px; background-color: rgba(0,0,0, .25);">
                                <v-icon>mdi-open-in-new</v-icon>
                            </v-btn>
                        </div>
                    </a>
                    <div v-else :style="`width: 100%; height: 100%; background-image: url(${img.url}); background-size: cover; background-position: center;`" />
                    <!-- <v-img :src="img.url" center contain></v-img> -->
                </v-carousel-item>
            </v-carousel>
        </div>

        
        <!-- Home Content -->

        <v-row>
            <v-col class="py-0" cols="12" lg="7">
                <v-card class="soft-shadow px-lg-2 py-lg-1 mb-3">
                    <v-card-title class="d-flex justify-space-between">
                        <div>Upcoming Class</div>
                        <div>
                            <v-btn :icon="phoneOnly" color="blue-grey darken-2" :loading="loading" :disabled="loading" :outlined="pcOnly" class="me-2" @click="$emit('load')">
                                <v-icon>mdi-reload</v-icon>
                            </v-btn>
                            <v-btn :icon="phoneOnly" color="blue-grey darken-2" :outlined="pcOnly" :to="{name: 'calendar'}" depressed>
                                <v-icon size="22" :left="pcOnly">mdi-calendar</v-icon>
                                <span v-if="pcOnly">Calendar</span>
                            </v-btn>
                        </div>
                    </v-card-title>
                    <v-card-subtitle v-if="pcOnly" class="mb-2">Open calendar to view more classes</v-card-subtitle>
                    <v-card-text>
                <!-- Upcoming Evaluation Banner -->
                <div v-if="nextEvaluation" class="mb-3">
                    <v-alert
                        text
                        :color="nextEvaluation.status === 'evaluation-coming' ? 'blue darken-1' : 'teal darken-1'"
                        border="left"
                        class="mb-0 pa-3"
                    >
                        <div class="d-flex align-center justify-space-between flex-wrap" style="gap:8px">
                            <div class="d-flex align-center" style="gap:10px">
                                <v-icon :color="nextEvaluation.status === 'evaluation-coming' ? 'blue darken-1' : 'teal darken-1'" size="28">mdi-clipboard-check-outline</v-icon>
                                <div>
                                    <div class="font-weight-semibold strong-text--text" style="font-size:0.95rem">Upcoming Evaluation Session</div>
                                    <div class="muted--text" style="font-size:0.82rem">{{ evalTimeFormatted }}</div>
                                </div>
                            </div>
                            <div class="d-flex align-center" style="gap:8px">
                                <v-chip small dark :color="nextEvaluation.status === 'evaluation-coming' ? 'blue darken-1' : 'teal darken-1'">
                                    {{ nextEvaluation.status === 'evaluation-coming' ? 'Happening Soon' : 'Scheduled' }}
                                </v-chip>
                                <v-btn
                                    v-if="canJoinEvaluation"
                                    small depressed dark color="blue darken-1"
                                    :loading="joiningEval"
                                    @click="joinEvaluation"
                                >
                                    <v-icon small left>mdi-video-outline</v-icon>
                                    Join Evaluation
                                </v-btn>
                            </div>
                        </div>
                    </v-alert>
                </div>

                <!-- Upcoming Consultation Banner -->
                <div v-if="nextConsultation" class="mb-3">
                    <v-alert
                        text
                        :color="nextConsultation.status === 'consultation-coming' ? 'lime darken-3' : 'lime darken-1'"
                        border="left"
                        class="mb-0 pa-3"
                    >
                        <div class="d-flex align-center justify-space-between flex-wrap" style="gap:8px">
                            <div class="d-flex align-center" style="gap:10px">
                                <v-icon :color="nextConsultation.status === 'consultation-coming' ? 'lime darken-3' : 'lime darken-1'" size="28">mdi-message-text-outline</v-icon>
                                <div>
                                    <div class="font-weight-semibold strong-text--text" style="font-size:0.95rem">Upcoming Consultation Session</div>
                                    <div class="muted--text" style="font-size:0.82rem">{{ consultTimeFormatted }}</div>
                                </div>
                            </div>
                            <div class="d-flex align-center" style="gap:8px">
                                <v-chip small dark :color="nextConsultation.status === 'consultation-coming' ? 'lime darken-3' : 'lime darken-1'">
                                    {{ nextConsultation.status === 'consultation-coming' ? 'Happening Soon' : 'Scheduled' }}
                                </v-chip>
                                <v-btn
                                    v-if="canJoinConsultation"
                                    small depressed dark color="lime darken-3"
                                    :loading="joiningConsult"
                                    @click="joinConsultation"
                                >
                                    <v-icon small left>mdi-video-outline</v-icon>
                                    Join Consultation
                                </v-btn>
                            </div>
                        </div>
                    </v-alert>
                </div>

                <v-alert v-if="!nextClass && !nextEvaluation && !nextConsultation" text color="blue-grey" type="info" :dense="phoneOnly">
                    <div :class="{'details-text': phoneOnly}">
                        It seems that you don't have classes...
                    </div>
                </v-alert>

                <upcoming-class-card v-if="nextClass" :class-data="nextClass" :reloading="loading" />
                    </v-card-text>
                </v-card>

            </v-col>
            <v-col class="py-0" cols="12" lg="5">
                <v-card v-if="appData.enable_app_w" class="soft-shadow px-2 py-1 mb-3">
                    <v-card-text>
                        <v-alert v-if="homeData.low_credits_alert" dense small text type="error" color="orange darken-2" class="px-3">
                            <div class="details-text font-weight-medium">
                                <b>Low Balance Alert:</b>
                                <br>
                                Please recharge as soon as possible...
                            </div>
                        </v-alert>

                        <div class="d-flex justify-space-between align-center w-full">

                            <div class="d-flex align-center">
                                <v-avatar size="60">
                                    <v-icon size="28" color="orange" class="orange lighten-5">mdi-wallet</v-icon>
                                </v-avatar>
                                <div class="ms-4">
                                    <div class="mb-1">Wallet Balance</div>
                                    <h2 class="strong-text--text font-weight-bold">{{moneyFormat(user.credits)}}</h2>
                                </div>
                            </div>
                            <div>
                                <v-btn outlined color="orange darken-1" dark depressed :to="{name: 'wallet'}">
                                    <v-icon size="22" :left="pcOnly">mdi-wallet</v-icon>
                                    <span v-if="pcOnly">Wallet</span>
                                </v-btn>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card class="soft-shadow px-2 py-1 mb-3">
                    <v-card-text>
                        <div class="d-flex justify-space-between align-center w-full">

                            <div class="d-flex align-center">
                                <v-avatar size="60">
                                    <v-icon size="28" color="green" class="green lighten-5">mdi-book-play</v-icon>
                                </v-avatar>
                                <div class="ms-4">
                                    <div class="mb-1">My Courses</div>
                                    <h2 class="strong-text--text font-weight-bold">{{user.my_courses ? user.my_courses.length : 0}}</h2>
                                </div>
                            </div>
                            <div>
                                <v-btn color="green darken-1" outlined dark depressed :to="{name: 'my-courses'}">
                                    <v-icon size="22" :left="pcOnly">mdi-book-play</v-icon>
                                    <span v-if="pcOnly">My Courses</span>
                                </v-btn>
                            </div>
                    </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12">
                <v-card class="soft-shadow px-2 py-1 mb-3">
                    <v-card-title>
                        <v-icon left color="teal darken-2">mdi-calendar-clock</v-icon>
                        Reschedule Requests
                        <span v-if="rescheduleRequests.length + myRescheduleRequests.length" class="teal--text text--darken-2 ms-2">({{rescheduleRequests.length + myRescheduleRequests.length}})</span>
                    </v-card-title>
                    <v-card-text :class="{'px-0': phoneOnly}">
                        <v-tabs v-model="rescheduleTab" color="teal darken-2" class="mb-3">
                            <v-tab>
                                Incoming Requests
                                <v-chip v-if="rescheduleRequests.length" x-small class="ms-2" color="teal" dark>{{rescheduleRequests.length}}</v-chip>
                            </v-tab>
                            <v-tab>
                                My Requests
                                <v-chip v-if="myRescheduleRequests.length" x-small class="ms-2" color="indigo" dark>{{myRescheduleRequests.length}}</v-chip>
                            </v-tab>
                        </v-tabs>
                        <v-tabs-items v-model="rescheduleTab" touchless>
                            <v-tab-item>
                                <v-alert v-if="!rescheduleRequests.length" text color="blue-grey" type="info" :dense="phoneOnly">
                                    <div :class="{'details-text': phoneOnly}">
                                        You don't have pending reschedule requests...
                                    </div>
                                </v-alert>
                                <div v-else>
                                    <reschedule-requests :requests="rescheduleRequests" @load="$emit('load')" />
                                </div>
                            </v-tab-item>
                            <v-tab-item>
                                <v-alert v-if="!myRescheduleRequests.length" text color="blue-grey" type="info" :dense="phoneOnly">
                                    <div :class="{'details-text': phoneOnly}">
                                        You don't have any sent reschedule requests...
                                    </div>
                                </v-alert>
                                <div v-else>
                                    <my-reschedule-requests :requests="myRescheduleRequests" @load="$emit('load')" />
                                </div>
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col v-if="cancelationRequests.length + myCancelationRequests.length" cols="12">
                <v-card class="soft-shadow px-2 py-1 mb-3">
                    <v-card-title>
                        <v-icon left color="red darken-2">mdi-close-circle-outline</v-icon>
                        Cancellation Requests
                        <span class="red--text text--darken-2 ms-2">({{cancelationRequests.length + myCancelationRequests.length}})</span>
                    </v-card-title>
                    <v-card-text :class="{'px-0': phoneOnly}">
                        <v-tabs v-model="cancelationTab" color="red darken-2" class="mb-3">
                            <v-tab>
                                Incoming Requests
                                <v-chip v-if="cancelationRequests.length" x-small class="ms-2" color="red" dark>{{cancelationRequests.length}}</v-chip>
                            </v-tab>
                            <v-tab>
                                My Requests
                                <v-chip v-if="myCancelationRequests.length" x-small class="ms-2" color="orange darken-2" dark>{{myCancelationRequests.length}}</v-chip>
                            </v-tab>
                        </v-tabs>
                        <v-tabs-items v-model="cancelationTab" touchless>
                            <v-tab-item>
                                <v-alert v-if="!cancelationRequests.length" text color="blue-grey" type="info" :dense="phoneOnly">
                                    <div :class="{'details-text': phoneOnly}">
                                        You don't have pending cancellation requests...
                                    </div>
                                </v-alert>
                                <div v-else>
                                    <cancelation-requests :requests="cancelationRequests" @load="$emit('load')" />
                                </div>
                            </v-tab-item>
                            <v-tab-item>
                                <v-alert v-if="!myCancelationRequests.length" text color="blue-grey" type="info" :dense="phoneOnly">
                                    <div :class="{'details-text': phoneOnly}">
                                        You don't have any sent cancellation requests...
                                    </div>
                                </v-alert>
                                <div v-else>
                                    <my-cancelation-requests :requests="myCancelationRequests" @load="$emit('load')" />
                                </div>
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12">
                <v-card class="soft-shadow px-2 py-1 mb-3">
                    <v-card-title>
                        <v-icon left color="deep-purple darken-1">mdi-calendar-plus</v-icon>
                        Makeup Class Requests
                        <span v-if="makeupClassRequests.length" class="deep-purple--text text--darken-1 ms-2">({{makeupClassRequests.length}})</span>
                    </v-card-title>
                    <v-card-text :class="{'px-0': phoneOnly && makeupClassRequests.length}">
                        <v-alert v-if="!makeupClassRequests.length" text color="blue-grey" type="info" :dense="phoneOnly">
                            <div :class="{'details-text': phoneOnly}">
                                You don't have pending makeup class requests...
                            </div>
                        </v-alert>
                        <div v-else>
                            <makeup-class-requests :requests="makeupClassRequests" @load="$emit('load')" />
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>


            <v-col cols="12">
                <v-card :flat="phoneOnly" class="px-lg-2 py-1 mb-lg-3" :class="{'transparent': phoneOnly, 'soft-shadow': pcOnly}">
                    <v-card-title>Featured Courses</v-card-title>
                    <v-card-text :class="{'px-0 pb-0': phoneOnly}">
                        <v-row v-if="homeData.featured_courses && homeData.featured_courses.length" :key="0">
                            <v-col v-for="course in homeData.featured_courses" :key="course.id" :cols="12" :md="6" :lg="4" :xl="3">
                                <course-card :course="course" style="height: 100%"/>
                            </v-col>
                            <v-col cols="12">
                                <v-btn v-if="phoneOnly" block :color="capRunning ? 'primary' : 'white'" :to="{name: 'courses'}" :depressed="capRunning">
                                    <v-icon size="20" left>mdi-view-grid</v-icon>
                                    Course Catalog
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-alert v-else text color="blue-grey" type="info">
                            No featured courses to show
                        </v-alert>

                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import moment from 'moment-timezone'

import CourseCard from '@/components/custom/course-card'
import UpcomingClassCard from '@/components/custom/upcoming-class-card'
import RescheduleRequests from './reschedule-requests'
import MyRescheduleRequests from './my-reschedule-requests'
import CancelationRequests from './cancelation-requests'
import MyCancelationRequests from './my-cancelation-requests'
import MakeupClassRequests from './makeup-class-requests'

import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

export default {
    props: {
        loading: {type: Boolean, default: false}
    },
    data(){
        return {
            interval: null,
            currentTime: Date.now(),
            joiningEval: false,
            joiningConsult: false,
            rescheduleTab: 0,
            cancelationTab: 0,
        }
    },
    components: {
        UpcomingClassCard,
        CourseCard,
        RescheduleRequests,
        MyRescheduleRequests,
        CancelationRequests,
        MyCancelationRequests,
        MakeupClassRequests
    },
    computed: {
        ...GlobalComputed,
        homeData(){
            return this.$store.state.userModule.homeData;
        },
        nextClass(){
            return this.homeData ? this.homeData.next_class : null;
        },
        nextEvaluation(){
            return this.homeData ? this.homeData.next_evaluation : null;
        },
        canJoinEvaluation(){
            if (!this.nextEvaluation || !this.nextEvaluation.meeting_link) return false;
            // Only upcoming evaluations
            if (this.nextEvaluation.status !== 'evaluation-coming' && this.nextEvaluation.status !== 'evaluation-soon') return false;
            
            // Re-evaluate every minute via currentTime
            const now = this.currentTime; 
            const joinTime = moment.utc(this.nextEvaluation.booked_start).subtract(15, 'minutes');
            return moment(now).isSameOrAfter(joinTime);
        },
        evalTimeFormatted(){
            if (!this.nextEvaluation) return '';
            const tz = moment.tz.guess();
            const start = moment.utc(this.nextEvaluation.booked_start).tz(tz);
            const end = moment.utc(this.nextEvaluation.booked_end).tz(tz);
            return start.format('ddd, MMM D · h:mm A') + ' – ' + end.format('h:mm A') + ' (' + tz + ')';
        },
        nextConsultation(){
            return this.homeData ? this.homeData.next_consultation : null;
        },
        canJoinConsultation(){
            if (!this.nextConsultation || !this.nextConsultation.meeting_link) return false;
            if (this.nextConsultation.status !== 'consultation-coming' && this.nextConsultation.status !== 'consultation-soon') return false;
            const now = this.currentTime;
            const joinTime = moment.utc(this.nextConsultation.booked_start).subtract(15, 'minutes');
            return moment(now).isSameOrAfter(joinTime);
        },
        consultTimeFormatted(){
            if (!this.nextConsultation) return '';
            const tz = moment.tz.guess();
            const start = moment.utc(this.nextConsultation.booked_start).tz(tz);
            const end = moment.utc(this.nextConsultation.booked_end).tz(tz);
            return start.format('ddd, MMM D · h:mm A') + ' – ' + end.format('h:mm A') + ' (' + tz + ')';
        },
        rescheduleRequests(){
            return this.homeData ? (this.homeData.reschedule_requests || []) : [];
        },
        myRescheduleRequests(){
            return this.homeData ? (this.homeData.my_reschedule_requests || []) : [];
        },
        cancelationRequests(){
            return this.homeData ? (this.homeData.cancelation_requests || []) : [];
        },
        myCancelationRequests(){
            return this.homeData ? (this.homeData.my_cancelation_requests || []) : [];
        },
        makeupClassRequests(){
            return this.homeData ? (this.homeData.makeup_class_requests || []) : [];
        },
        slideImages(){
            return this.homeData ? this.homeData.slide_images : [];
        }
    },
    methods: {
        joinEvaluation(){
            if (this.joiningEval || !this.nextEvaluation) return;
            this.joiningEval = true;
            window.axios.post(`/evaluations/${this.nextEvaluation.id}/join`).then(response => {
                this.joiningEval = false;
                const url = response.data.meeting_link;
                window.open(url, '_blank');
            }).catch(e => {
                this.joiningEval = false;
                this.showMsg('Error occurred during joining, please try again', 'error');
            });
        },
        joinConsultation(){
            if (this.joiningConsult || !this.nextConsultation) return;
            this.joiningConsult = true;
            window.axios.post(`/evaluations/${this.nextConsultation.id}/join`).then(response => {
                this.joiningConsult = false;
                const url = response.data.meeting_link;
                window.open(url, '_blank');
            }).catch(e => {
                this.joiningConsult = false;
                this.showMsg('Error occurred during joining, please try again', 'error');
            });
        },
        ...GlobalMethods
    },
    created(){
        const timeout = this.appData.home_reload_timeout || 10000;
        this.interval = window.setInterval(()=>{
            this.$emit('load');
        }, timeout)
    },
    mounted(){
        this.interval = window.setInterval(() => {
            this.currentTime = Date.now();
        }, 60000);

        if (this.isStudent && (!this.user.my_courses || this.user.my_courses.length === 0)){
            this.$router.push({name: 'courses'});
        }
    },
    beforeDestroy(){
        window.clearInterval(this.interval);
    }
}
</script>

<style lang="scss" scoped>
    .header-carousel{
        border-radius: 5px;
        border: 1px solid rgba(#000, 0.1);

        @media(min-width: 768px){
            box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.25) !important;
            border-radius: 13px;
        }
    }
</style>
<style lang="scss">
.v-window__prev, .v-window__next{
    background: none!important;
}
.v-carousel__controls{
    background: none!important;
    .v-carousel__controls__item{
        margin: 0 2px!important;

        .v-icon.mdi-circle{
            font-size: 15px!important;
        }
    }
}
</style>
